import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContactInformationForm } from '@/pages/private/my-account/ContactInformationForm.jsx';
import { useAuthMe } from '@/store/auth/selectors';
import { fetchMe } from '@/store/auth/thunks';
import { PageLayout } from '@/components/PageLayout';
import { ChangePasswordForm } from './ChangePasswordForm';
import { TaxInformationForm } from './TaxInformationForm.jsx';
import { TABS } from './constants';
import Language from './Language';
import Currency from './Currency';
import DeleteAccount from './DeleteAccount';
import { Billing } from './billing';

export function MyAccount() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const authMe = useSelector(useAuthMe);
	const dispatch = useDispatch();
	const locationTab = location.state?.tab;

	const [view, setView] = useState(
		() => locationTab || TABS.GENERAL_INFORMATION
	);

	useEffect(() => {
		if (!authMe.loading) {
			dispatch(fetchMe());
		}
	}, []);

	useEffect(() => {
		if (locationTab) {
			setView(locationTab);

			navigate(location.pathname, {
				replace: true,
				state: {}
			});
		}
	}, [locationTab]);

	useEffect(
		() => () => {
			setView(TABS.GENERAL_INFORMATION);
		},
		[]
	);

	const userData = authMe?.data;
	const viewsPermissions = userData?.views;
	const isOwner = !userData?.parentAccount;
	const ACCOUNT_TABS = [
		{
			value: TABS.GENERAL_INFORMATION,
			label: 'myAccount.generalInformation',
			showTab: true
		},
		{
			value: TABS.TAX_INFORMATION,
			label: 'myAccount.taxInformation.title',
			showTab: isOwner || viewsPermissions?.taxInfo
		},
		{
			value: TABS.BILLING,
			label: 'pageTitles.billing',
			showTab: isOwner || viewsPermissions?.billing
		}
	];

	const isBillingTab = view === TABS.BILLING;

	return (
		<PageLayout title={t('pageTitles.myAccount')}>
			<Tabs
				allowScrollButtonsMobile
				onChange={(_, v) => setView(v)}
				value={view}
				variant="scrollable"
				scrollButtons="auto"
			>
				{ACCOUNT_TABS.map(
					tab =>
						tab.showTab && (
							<Tab
								key={tab.value}
								value={tab.value}
								label={t(tab.label)}
							/>
						)
				)}
			</Tabs>
			<Box
				sx={{
					width: '100%',
					maxWidth: isBillingTab ? '792px' : '590px',
					mt: {
						xs: isBillingTab ? '28px' : '32px',
						md: isBillingTab ? '40px' : '56px'
					}
				}}
			>
				{view === TABS.GENERAL_INFORMATION && (
					<Stack spacing={{ xs: 5, md: 7 }}>
						<ContactInformationForm />
						<ChangePasswordForm />
						<Language />
						<Currency />
						<DeleteAccount />
					</Stack>
				)}
				{view === TABS.TAX_INFORMATION && (
					<TaxInformationForm mt={-2} sx={{ width: '100%' }} />
				)}
				{isBillingTab && <Billing />}
			</Box>
		</PageLayout>
	);
}
